import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * VideoPreviewGallery
 *
 * A modern, responsive gallery that shows video cards with a short, silent
 * autoplaying preview when the user hovers (desktop) or taps a "Preview" button (mobile).
 *
 * ✅ Smooth hover preview (muted, loop, playsInline)
 * ✅ Lazy loading (IntersectionObserver)
 * ✅ Keyboard accessible (focus triggers preview)
 * ✅ Mobile friendly (explicit Preview button when there's no hover)
 * ✅ Tailwind styling (no external UI libs required)
 * ✅ Replace the sample `items` with your own URLs (poster, src, preview)
 *
 * Notes:
 * - For best UX, generate a small ~2–5s preview clip for each video
 *   (e.g., myvideo_preview.mp4) and a JPG poster image.
 * - Ensure your bucket/CDN allows HTTP range requests & correct MIME types (video/mp4, image/jpeg).
 * - On iOS, `playsInline` avoids fullscreen on play.
 */

/** Sample data. Replace with your own items. */
const sampleItems = [
  {
    id: "1",
    title: "Drone over mountains",
    poster: "https://cdn.example.com/videos/mountains_poster.jpg",
    src: "https://cdn.example.com/videos/mountains.mp4",
    preview: "https://cdn.example.com/videos/mountains_preview.mp4",
    href: "#",
    duration: "03:24",
  },
  {
    id: "2",
    title: "City timelapse",
    poster: "https://cdn.example.com/videos/city_poster.jpg",
    src: "https://cdn.example.com/videos/city.mp4",
    preview: "https://cdn.example.com/videos/city_preview.mp4",
    href: "#",
    duration: "01:18",
  },
  {
    id: "3",
    title: "Surf session",
    poster: "https://cdn.example.com/videos/surf_poster.jpg",
    src: "https://cdn.example.com/videos/surf.mp4",
    preview: "https://cdn.example.com/videos/surf_preview.mp4",
    href: "#",
    duration: "05:07",
  },
  {
    id: "4",
    title: "Forest walk",
    poster: "https://cdn.example.com/videos/forest_poster.jpg",
    src: "https://cdn.example.com/videos/forest.mp4",
    preview: "https://cdn.example.com/videos/forest_preview.mp4",
    href: "#",
    duration: "02:02",
  },
];

/** Utility: detect if device likely supports hover */
function useSupportsHover() {
  const [supportsHover, setSupportsHover] = useState(false);
  useEffect(() => {
    if (typeof window !== "undefined" && window.matchMedia) {
      const mq = window.matchMedia("(hover: hover)");
      setSupportsHover(mq.matches);
      const onChange = (e) => setSupportsHover(e.matches);
      mq.addEventListener?.("change", onChange);
      return () => mq.removeEventListener?.("change", onChange);
    }
  }, []);
  return supportsHover;
}

/** Utility: respect reduced motion */
function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window !== "undefined" && window.matchMedia) {
      const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
      setReduced(mq.matches);
      const onChange = (e) => setReduced(e.matches);
      mq.addEventListener?.("change", onChange);
      return () => mq.removeEventListener?.("change", onChange);
    }
  }, []);
  return reduced;
}

/** Hook: lazy-observe a DOM node */
function useOnScreen(ref, rootMargin = "0px") {
  const [isIntersecting, setIntersecting] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => setIntersecting(entry.isIntersecting),
      { rootMargin }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref, rootMargin]);
  return isIntersecting;
}

function VideoCard({ item, enableHoverPreview, reducedMotion }) {
  const { title, poster, src, preview, href, duration } = item;
  const cardRef = useRef(null);
  const videoRef = useRef(null);
  const [isHovering, setIsHovering] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [didStart, setDidStart] = useState(false);

  const visible = useOnScreen(cardRef, "200px");

  // Decide which source to load for the <video>. We only attach a src when visible.
  const videoSrc = useMemo(() => {
    if (!visible) return "";
    // When hovering/focused, prefer the lightweight preview clip; otherwise keep empty to avoid data usage.
    return isHovering && enableHoverPreview && !reducedMotion ? preview : "";
  }, [visible, isHovering, enableHoverPreview, reducedMotion, preview]);

  // Apply src changes to the <video> element directly to avoid extra <source> tags.
  useEffect(() => {
    if (!videoRef.current) return;
    const v = videoRef.current;
    if (v.src !== videoSrc) {
      v.pause();
      v.removeAttribute("src");
      if (videoSrc) {
        v.src = videoSrc;
        v.load();
      }
      setIsLoaded(false);
      setDidStart(false);
    }
  }, [videoSrc]);

  // Autoplay when we have a src and user is hovering/focused.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (videoSrc && isHovering && enableHoverPreview && !reducedMotion) {
      const play = async () => {
        try {
          await v.play();
          setDidStart(true);
        } catch (err) {
          // Autoplay might be blocked; ignore.
        }
      };
      play();
    } else {
      v.pause();
      v.currentTime = 0;
      setDidStart(false);
    }
  }, [videoSrc, isHovering, enableHoverPreview, reducedMotion]);

  const onEnter = () => setIsHovering(true);
  const onLeave = () => setIsHovering(false);

  const onKey = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setIsHovering((prev) => !prev);
    }
  };

  const showVideoLayer = enableHoverPreview && !reducedMotion && (didStart || isHovering);

  return (
    <article
      ref={cardRef}
      className="group relative overflow-hidden rounded-2xl bg-neutral-900 ring-1 ring-white/10 shadow-lg transition hover:shadow-2xl focus-within:shadow-2xl"
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onFocus={onEnter}
      onBlur={onLeave}
      onKeyDown={onKey}
      tabIndex={0}
      aria-label={`${title} – preview`}
    >
      {/* Poster image (always rendered) */}
      <img
        src={poster}
        alt={title}
        loading="lazy"
        className={`h-48 sm:h-56 md:h-64 w-full object-cover transition duration-300 ${
          showVideoLayer ? "opacity-0" : "opacity-100"
        }`}
      />

      {/* Video preview layer */}
      <video
        ref={videoRef}
        muted
        loop
        playsInline
        preload="metadata"
        onLoadedData={() => setIsLoaded(true)}
        className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-200 ${
          showVideoLayer && isLoaded ? "opacity-100" : "opacity-0"
        }`}
      />

      {/* Top-left badge */}
      <div className="absolute left-3 top-3 z-10">
        <span className="rounded-full bg-black/50 px-2 py-1 text-xs text-white backdrop-blur-sm">
          Preview
        </span>
      </div>

      {/* Bottom gradient with title + duration */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-black/70 via-black/0 to-transparent p-3">
        <div className="flex items-end justify-between">
          <h3 className="text-white text-sm sm:text-base font-medium drop-shadow-md line-clamp-2 pr-2">
            {title}
          </h3>
          {duration && (
            <span className="text-white/90 text-xs font-mono bg-black/40 rounded px-1.5 py-0.5">
              {duration}
            </span>
          )}
        </div>
      </div>

      {/* Mobile-only explicit preview button */}
      {!enableHoverPreview && (
        <div className="absolute inset-0 z-10 flex items-center justify-center">
          <button
            className="rounded-full bg-white/90 px-4 py-2 text-sm font-medium text-black shadow hover:bg-white"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setIsHovering((prev) => !prev);
            }}
          >
            {isHovering ? "Parar preview" : "Preview"}
          </button>
        </div>
      )}

      {/* Click through to the full video/page */}
      {href && (
        <a
          href={href}
          className="absolute inset-0 z-0"
          aria-label={`Abrir ${title}`}
        />
      )}
    </article>
  );
}

export default function VideoPreviewGallery({ items = sampleItems, title = "Vídeos" }) {
  const supportsHover = useSupportsHover();
  const reducedMotion = usePrefersReducedMotion();

  const enableHoverPreview = supportsHover && !reducedMotion;

  return (
    <div className="mx-auto max-w-7xl px-4 py-6">
      <header className="mb-4 flex items-end justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-white">{title}</h2>
          <p className="text-sm text-white/70">
            Passe o mouse para ver um preview curto (mudo). Em dispositivos
            touch, use o botão Preview.
          </p>
        </div>
        <div className="hidden sm:block text-xs text-white/60">
          {enableHoverPreview ? "Hover ativo" : "Preview por botão (touch/reduzido)"}
        </div>
      </header>

      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {items.map((item) => (
          <VideoCard
            key={item.id}
            item={item}
            enableHoverPreview={enableHoverPreview}
            reducedMotion={reducedMotion}
          />
        ))}
      </section>
    </div>
  );
}

/**
 * --- How to use ---
 * 1) Drop this file into your React project and import it.
 * 2) Replace `sampleItems` with your real data.
 * 3) Ensure each item has: { id, title, poster, src, preview, href, duration }
 * 4) Style is Tailwind-based. Remove classes if you don't use Tailwind.
 *
 * --- Optional: generate preview clip & poster with FFmpeg ---
 * // 2–4 second lightweight preview (H.264, no audio, web-friendly)
 * ffmpeg -ss 00:00:08 -i input.mp4 -t 4 \
 *   -vf "scale=640:-2,fps=24" -an -c:v libx264 -preset veryfast -crf 28 \
 *   -movflags +faststart output_preview.mp4
 *
 * // Poster image (frame grab)
 * ffmpeg -ss 00:00:05 -i input.mp4 -vframes 1 -vf "scale=1280:-2" poster.jpg
 *
 * --- S3/MinIO CORS hint ---
 * Allow GET/HEAD and expose range headers so the browser can seek:
 * [
 *   {
 *     "AllowedOrigins": ["*"],
 *     "AllowedMethods": ["GET", "HEAD"],
 *     "AllowedHeaders": ["*"],
 *     "ExposeHeaders": ["Accept-Ranges", "Content-Range", "Content-Length"],
 *     "MaxAgeSeconds": 3000
 *   }
 * ]
 */
